import React, { useMemo, useState } from "react";
import { create } from "zustand";
import useSWR from "swr";
import {
  getCoreRowModel,
  getSortedRowModel,
  useReactTable,
  SortingState,
} from "@tanstack/react-table";
import { Card, CardContent } from "@/components/ui/card";
import Loader from "@/components/ui/Loader";
import EmptyDatas from "components/ui/EmptyDatas";
import Table from "components/ui/my-data-table";
import { columns } from "./data-table-column";
import DataTableToolbar from "./data-table-toolbar";
import { BoneTreePanel } from "./components/BoneTreePanel";

interface FormState {
  after: string | null;
  afters: string[];
  goAfter: (nextAfter: string | null) => void;
}

export const useForm = create<FormState>((set) => ({
  after: null,
  afters: [],
  goAfter: (nextAfter) =>
    set((state) => {
      if (nextAfter === null) {
        return { after: null };
      }
      const index = state.afters.findIndex((a) => a === nextAfter);
      if (index === -1) {
        return { after: nextAfter, afters: [...state.afters, nextAfter] };
      } else {
        return { after: nextAfter };
      }
    }),
}));

const LayerIcons: React.FC = () => {
  const [sorting, setSorting] = useState<SortingState>([]);
  const [selectedBone, setSelectedBone] = useState("root");
  const after = useForm((s) => s.after);

  const params = useMemo(() => {
    const queryParams: Record<string, string | null> = { after };
    // 如果选择了骨骼类型，添加到查询参数中
    if (selectedBone) {
      queryParams.bone = selectedBone;
    }
    return queryParams;
  }, [after, selectedBone]);

  const {
    data: globalIcons = [],
    isLoading,
    isValidating,
    mutate,
  } = useSWR(["/layericons", params]);
  const nextAfter = (globalIcons as any[])?.[(globalIcons as any[])?.length - 1]
    ?.id;

  const table = useReactTable({
    data: globalIcons as any[],
    columns,
    enableRowSelection: false,
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    state: {
      sorting,
    },
  });

  return (
    <div className="flex-1 flex flex-col h-full gap-2">
      <div className="flex-none">
        <DataTableToolbar mutate={mutate} nextAfter={nextAfter} />
      </div>
      <div className="flex-1 flex gap-4 !max-h-[calc(100vh-254px)] overflow-auto">
        {/* 左侧树形面板 */}
        <div className="w-64 flex-shrink-0">
          <BoneTreePanel value={selectedBone} onChange={setSelectedBone} />
        </div>
        {/* 右侧表格 */}
        <div className="flex-1">
          <Card className="h-full flex flex-col overflow-auto">
            <CardContent className="flex-1 pb-0">
              {isLoading || isValidating ? (
                <div className="flex items-center justify-center h-full">
                  <Loader />
                </div>
              ) : !globalIcons || (globalIcons as any[]).length === 0 ? (
                <div className="flex items-center justify-center h-full">
                  <EmptyDatas />
                </div>
              ) : (
                <Table table={table} />
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default LayerIcons;
